import { useEffect, useRef, useCallback } from 'react';
import useIntercomStore from '../stores/intercomStore';
import useAuthStore from '../stores/authStore';

const playNotificationSound = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    oscillator.frequency.value = 800;
    oscillator.type = 'sine';
    gainNode.gain.value = 0.3;
    oscillator.start();
    oscillator.stop(audioContext.currentTime + 0.2);
    setTimeout(() => {
      const osc2 = audioContext.createOscillator();
      osc2.connect(gainNode);
      osc2.frequency.value = 1000;
      osc2.type = 'sine';
      osc2.start();
      osc2.stop(audioContext.currentTime + 0.2);
    }, 250);
  } catch (e) { console.log('Could not play sound:', e); }
};

export function useIntercomNotifications(enabled = true) {
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const { tenant, user } = useAuthStore();
  const tenantId = tenant?.id || 'default';
  const { addNotification, markAnswered, removeNotification, setConnected, isConnected, notifications } = useIntercomStore();

  const connect = useCallback(() => {
    if (!enabled || wsRef.current?.readyState === WebSocket.OPEN) return;
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${protocol}//${window.location.host}/ws/intercom?tenantId=${tenantId}`;
    console.log('[Intercom WS] Connecting to:', wsUrl);
    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      ws.onopen = () => { console.log('[Intercom WS] Connected'); setConnected(true); reconnectAttempts.current = 0; };
      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          console.log('[Intercom WS] Received:', message.type);
          
          switch (message.type) {
            case 'INTERCOM_CALL':
              // Legacy notification from webhook - add to store for in-app toast
              // Push notifications are handled separately by the service worker
              addNotification(message.payload);
              playNotificationSound();
              // Note: Browser notification removed - push notifications handle this now
              break;
              
            case 'intercom:call:started':
              // New persistent notification system - update notification with callId
              console.log('[Intercom WS] Call started:', message.payload);
              // The INTERCOM_CALL event adds the notification, this just adds the callId
              // We need to update the notification with the callId for acknowledgment
              const { callId, intercomId } = message.payload;
              // Store callId mapping (intercomId -> callId) for acknowledgment
              useIntercomStore.getState().setCallId(intercomId, callId);
              break;
              
            case 'intercom:call:acknowledged':
              // Someone acknowledged the call - remove notification for all users
              console.log('[Intercom WS] Call acknowledged:', message.payload);
              const { callId: ackCallId, acknowledgedByName, action } = message.payload;
              // Find and remove notification by callId or intercomId
              const notifs = useIntercomStore.getState().notifications;
              const notifToRemove = notifs.find(n => 
                n.callId === ackCallId || 
                useIntercomStore.getState().callIdMap?.[n.intercomId] === ackCallId
              );
              if (notifToRemove) {
                if (action === 'answer') {
                  markAnswered(notifToRemove.id, acknowledgedByName);
                }
                // Remove after a short delay so user sees the "answered by" status
                setTimeout(() => removeNotification(notifToRemove.id), action === 'answer' ? 2000 : 0);
              }
              break;
              
            case 'intercom:call:ended':
              // Call expired or timed out
              console.log('[Intercom WS] Call ended:', message.payload);
              break;
              
            case 'INTERCOM_CALL_ANSWERED':
              // Legacy answered event
              markAnswered(message.payload.notificationId, message.payload.answeredBy);
              break;
              
            case 'CONNECTED':
            case 'PONG':
              // Ignore these
              break;
              
            default:
              console.log('[Intercom WS] Unknown message type:', message.type);
          }
        } catch (error) { console.error('[Intercom WS] Parse error:', error); }
      };
      ws.onclose = () => {
        setConnected(false); wsRef.current = null;
        if (enabled) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
          reconnectAttempts.current++;
          reconnectTimeoutRef.current = setTimeout(connect, delay);
        }
      };
      ws.onerror = (error) => console.error('[Intercom WS] Error:', error);
    } catch (error) { console.error('[Intercom WS] Failed:', error); }
  }, [enabled, tenantId, addNotification, markAnswered, removeNotification, setConnected]);

  useEffect(() => {
    if (enabled) {
      if ('Notification' in window && Notification.permission === 'default') Notification.requestPermission();
      connect();
    }
    return () => {
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
      if (wsRef.current) { wsRef.current.close(); wsRef.current = null; }
    };
  }, [enabled, connect]);

  useEffect(() => {
    if (!enabled) return;
    const pingInterval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) wsRef.current.send(JSON.stringify({ type: 'PING' }));
    }, 25000);
    return () => clearInterval(pingInterval);
  }, [enabled]);

  return { isConnected, notifications, reconnect: connect };
}
export default useIntercomNotifications;
